"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { it } from "../locales/it";
import { en } from "../locales/en";
import { es } from "../locales/es";
import { fr } from "../locales/fr";
import { de } from "../locales/de";

type Language = "it" | "en" | "es" | "fr" | "de";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, replacements?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const dictionaries: Record<Language, any> = {
  it,
  en,
  es,
  fr,
  de,
};

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<Language>("it");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("language") as Language;
      if (stored === "it" || stored === "en") {
        setLanguageState(stored);
      } else {
        // Fallback to browser language
        const browserLang = navigator.language.split("-")[0];
        if (browserLang === "it" || browserLang === "en") {
          setLanguageState(browserLang);
        }
      }
      setMounted(true);
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      localStorage.setItem("language", lang);
    }
  };

  const t = (key: string, replacements?: Record<string, string | number>): string => {
    const keys = key.split(".");
    
    // Resolve from active dictionary
    let value = keys.reduce((acc, k) => acc?.[k], dictionaries[language]);
    
    // Fallback to Italian if missing
    if (value === undefined && language !== "it") {
      value = keys.reduce((acc, k) => acc?.[k], dictionaries["it"]);
    }
    
    // Fallback to key if still missing
    if (value === undefined) {
      return key;
    }

    if (typeof value !== "string") {
      return String(value);
    }

    // Handle replacements
    if (replacements) {
      let result = value;
      Object.entries(replacements).forEach(([k, v]) => {
        result = result.replace(new RegExp(`{${k}}`, "g"), String(v));
      });
      return result;
    }

    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
